/**
 * Clients API
 * API для работы с клиентами CRM
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ClientDataServiceApi, type V1ManagerInfo } from '@/api/clients/client'
import { Configuration } from '@/api/clients/client/configuration'
import { apiClient } from '@/api'
import type { TasksTask } from '@/api/clients/tasks'
import {
  tasksServiceApi,
  statusFor,
  isCompleted,
  TASK_ENTITY_CLIENT,
} from '@/features/tasks/api/tasks.api'
import {
  type Client,
  type CreateClientRequest,
  type UpdateClientRequest,
  type ClientsFilter,
  type PaginationParams,
  type PaginatedClients,
  type ClientChangeLog,
  type ClientFile,
  type ImportResult,
  type ClientTag,
  type ClientTask,
  type CreateClientTaskRequest,
  type UpdateClientTaskRequest,
  ClientChangeActionType,
} from '@/features/clients/types/client.types'
import { toApiMoney } from '@/utils/price'
import { mapClientFromAPI } from '@/features/clients/mappers/client.mapper'

// Создаем экземпляр API
const clientDataApi = new ClientDataServiceApi(new Configuration(), undefined, apiClient)

/**
 * Конвертирует дату из формата YYYY-MM-DD в RFC3339 Timestamp формат
 * @param dateString - дата в формате YYYY-MM-DD (например, "1999-02-12")
 * @returns дата в формате RFC3339 (например, "1999-02-12T00:00:00Z") или undefined
 */
function convertDateToTimestamp(dateString?: string): string | undefined {
  if (!dateString) return undefined
  // Добавляем время в полночь по UTC
  return `${dateString}T00:00:00Z`
}

/**
 * DEV-1991. Маппинг задачи клиента из сервиса задач в доменный тип.
 *
 * clientId передаём отдельно: у сервиса задач владелец лежит в entity_id, и
 * доверять ему вслепую нельзя — карточка спрашивала задачи конкретного клиента.
 */
function mapServiceClientTask(clientId: string, task: TasksTask): ClientTask {
  return {
    id: task.id || '',
    clientId: task.entityId || clientId,
    description: task.description || '',
    dueDate: task.dueDate,
    dueHasTime: task.dueHasTime,
    reminderMinutes: task.reminderMinutes,
    assigneeManagerId: task.assigneeManagerId,
    // Раньше закрытость выводилась только из отметки: статуса у client_tasks не
    // было вовсе. Теперь он есть, и смотреть надо на оба — импорт из зеркала
    // выводил статус ровно из completed_at (DEV-1985).
    completed: isCompleted(task),
    completedAt: task.completedAt,
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    taskTypeId: task.taskTypeId || undefined,
    taskTypeName: task.taskType?.name || undefined,
    taskTypeColor: task.taskType?.colorToken || undefined,
    taskTypeIcon: task.taskType?.icon || undefined,
  }
}

export const clientsApi = {
  /**
   * Получить список клиентов с пагинацией и фильтрами
   * GET /api/v1/clients
   */
  async getClients(
    filter?: ClientsFilter,
    pagination?: PaginationParams,
  ): Promise<PaginatedClients> {
    const page = pagination?.page?.toString()
    const perPage = pagination?.pageSize?.toString()

    const response = await clientDataApi.clientDataServiceListClientData(
      page,
      perPage,
      filter?.responsibleManagerId,
      undefined, // leadSource (deprecated)
      undefined, // statuses (DEV-42: client_statuses CRUD removed)
      undefined, // leadSources — DEV-2301: источник фильтруется ветками sourceKeys
      filter?.createdAfter,
      filter?.createdBefore,
      filter?.search,
      filter?.language,
      filter?.hasDeal, // hasDeal (DEV-254: фильтр активной сделки резолвится на бэкенде через deals)
      filter?.isArchived, // DEV-1061: архивные показываем только по явному фильтру
      filter?.hasResponsibleManager,
      filter?.agentId, // DEV-232: фильтр по агенту, приведшему клиента
      filter?.hasAgent, // DEV-281: пресет «Агентские» — клиенты с любым агентом
      filter?.agentDuplicateStatus, // DEV-1005: пресет «Дубли» — agent_duplicate_status
      filter?.tagKeys, // DEV-1062: фильтр по тегам клиента (WARMING/TEST/...)
      filter?.archiveReasonIds, // DEV-1061: фильтр по причине архивации
      filter?.cityIds, // DEV-853: фильтр по городам справочника client-data
      undefined, // countryId
      filter?.sourceKeys?.length ? filter.sourceKeys : undefined, // DEV-2301: ветки «Источника»
    )

    const clients = (response.data.clients || []).map(mapClientFromAPI)
    const total = response.data.totalCount || 0

    return {
      clients,
      total,
      page: pagination?.page || 1,
      pageSize: pagination?.pageSize || 20,
      totalPages: Math.ceil(total / (pagination?.pageSize || 20)),
    }
  },

  /**
   * Выгрузить клиентов файлом по текущим фильтрам.
   * GET /api/v1/clients/export?format=csv|xlsx
   * Эндпоинт реализован прямо в chi-роутере client-data, не в proto, поэтому
   * генератор API-клиента его не знает — обращаемся через apiClient напрямую.
   */
  async exportClients(filter: ClientsFilter, format: 'csv' | 'xlsx'): Promise<Blob> {
    const params = new URLSearchParams()
    params.set('format', format)
    if (filter.search) params.set('search', filter.search)
    for (const key of filter.sourceKeys ?? []) params.append('sourceKeys', key)
    if (filter.responsibleManagerId) params.set('manager', filter.responsibleManagerId)
    if (filter.language) params.set('language', filter.language)
    if (filter.createdAfter) params.set('createdFrom', filter.createdAfter)
    if (filter.createdBefore) params.set('createdTo', filter.createdBefore)
    if (filter.hasDeal !== undefined) params.set('hasDeal', String(filter.hasDeal))
    if (filter.isArchived !== undefined) params.set('isArchived', String(filter.isArchived))
    // DEV-1062/DEV-1061: те же фильтры, что в листинге, — выгрузка обязана совпадать с экраном.
    filter.tagKeys?.forEach((key) => params.append('tagKeys', key))
    filter.archiveReasonIds?.forEach((id) => params.append('archiveReasons', id))
    // DEV-853: город — тот же фильтр, что и в листинге.
    filter.cityIds?.forEach((id) => params.append('cityIds', id))

    const response = await apiClient.get<Blob>(`/api/v1/clients/export?${params.toString()}`, {
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Импортировать клиентов из файла (CSV/XLSX).
   * POST /api/v1/clients/import (multipart/form-data, поле "file").
   * Эндпоинт реализован прямо в chi-роутере client-data, не в proto, поэтому
   * генератор API-клиента его не знает — обращаемся через apiClient напрямую.
   */
  async importClients(file: File, format: 'csv' | 'xlsx'): Promise<ImportResult> {
    const form = new FormData()
    form.append('file', file)
    form.append('format', format)

    // RowError на бэкенде без json-тегов → ключи приходят как Line/Reason.
    type RawRowError = { Line: number; Reason: string }
    type RawImportResult = Omit<ImportResult, 'errors'> & { errors?: RawRowError[] }

    const response = await apiClient.post<RawImportResult>('/api/v1/clients/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    const data = response.data
    return {
      total: data.total,
      created: data.created,
      merged: data.merged,
      skipped: data.skipped,
      failed: data.failed,
      errors: (data.errors ?? []).map((rowError) => ({
        line: rowError.Line,
        reason: rowError.Reason,
      })),
    }
  },

  /**
   * Получить список менеджеров (client-data.GetManagers).
   *
   * DEV-699. Без role — менеджеры (только admin/hos, как раньше);
   * role=support_manager — носители роли «Сопровождающий менеджер», метод
   * доступен и обычному менеджеру. Параметр ещё не в generated-клиенте
   * (регенерация после мержа proto), поэтому передаём его axios-опциями.
   */
  async getManagers(role?: 'support_manager'): Promise<V1ManagerInfo[]> {
    const response = await clientDataApi.clientDataServiceGetManagers(role)
    return response.data.managers || []
  },

  /**
   * Получить клиента по ID
   * GET /api/v1/clients/{id}
   */
  async getClientById(id: string): Promise<Client> {
    const response = await clientDataApi.clientDataServiceGetClient(id)
    return mapClientFromAPI(response.data)
  },

  /**
   * Создать нового клиента
   * POST /api/v1/clients
   */
  async createClient(data: CreateClientRequest): Promise<Client> {
    // Debug: проверяем конвертацию даты
    const convertedDate = convertDateToTimestamp(data.dateOfBirth)
    console.log('[DEBUG] Original dateOfBirth:', data.dateOfBirth)
    console.log('[DEBUG] Converted dateOfBirth:', convertedDate)

    const requestPayload = {
      // Основная информация
      firstName: data.firstName,
      lastName: data.lastName,
      contacts: data.contacts.map((c) => ({
        type: c.type as any,
        value: c.value,
      })),
      countryId: data.countryId,
      cityId: data.cityId,
      city: data.city,
      language: data.language || 'ru',
      leadSourceId: data.leadSourceId,
      preferredContactMethod: data.preferredContactMethod as any,
      dateOfBirth: convertedDate,

      // DEV-699. Ответственный менеджер при создании (опционально): пусто —
      // распределится автоматически. Раньше назначался отдельным UpdateClient.
      responsibleManagerId: data.responsibleManagerId,

      // Интересы и потребности
      interestedPropertyTypes: data.interestedPropertyTypes as any,
      interestedLocations: data.interestedLocations,
      budget: data.budget
        ? {
            // DEV-301: каноника Money (валюта ввода, минорные единицы)
            moneyFrom: toApiMoney(data.budget.moneyFrom) as any,
            moneyTo: toApiMoney(data.budget.moneyTo) as any,
          }
        : undefined,
      purchaseGoal: data.purchaseGoal as any,
      dealTimeline: data.dealTimeline as any,
      additionalCriteria: data.additionalCriteria,

      // Маркетинг
      isSubscribedToNewsletter: data.isSubscribedToNewsletter,
    }

    console.log('[DEBUG] Request payload:', JSON.stringify(requestPayload, null, 2))

    const response = await clientDataApi.clientDataServiceCreateClient(requestPayload)

    return mapClientFromAPI(response.data)
  },

  /**
   * Обновить клиента
   * PATCH /api/v1/clients/{id}
   */
  async updateClient(data: UpdateClientRequest): Promise<Client> {
    // Формируем payload только с теми полями, которые были переданы
    const payload: Record<string, any> = {}

    if (data.firstName !== undefined) payload.firstName = data.firstName
    if (data.lastName !== undefined) payload.lastName = data.lastName
    if (data.dateOfBirth !== undefined) payload.dateOfBirth = data.dateOfBirth
    if (data.countryId !== undefined) payload.countryId = data.countryId
    if (data.cityId !== undefined) payload.cityId = data.cityId
    if (data.city !== undefined) payload.city = data.city
    if (data.language !== undefined) payload.language = data.language
    if (data.contacts !== undefined) {
      payload.contacts = data.contacts.map((c) => ({
        type: c.type as any,
        value: c.value,
      }))
    }
    if (data.preferredContactMethod !== undefined)
      payload.preferredContactMethod = data.preferredContactMethod as any
    if (data.leadSourceId !== undefined) payload.leadSourceId = data.leadSourceId
    if (data.responsibleManagerId !== undefined)
      payload.responsibleManagerId = data.responsibleManagerId
    if (data.interestedPropertyTypes !== undefined)
      payload.interestedPropertyTypes = data.interestedPropertyTypes as any
    if (data.interestedLocations !== undefined)
      payload.interestedLocations = data.interestedLocations
    if (data.budget !== undefined) {
      // DEV-301: бюджет уезжает Money-полями (legacy budgetFrom/To/Currency удалены из API)
      if (data.budget.moneyFrom !== undefined)
        payload.budgetMoneyFrom = toApiMoney(data.budget.moneyFrom)
      if (data.budget.moneyTo !== undefined) payload.budgetMoneyTo = toApiMoney(data.budget.moneyTo)
    }
    if (data.purchaseGoal !== undefined) payload.purchaseGoal = data.purchaseGoal as any
    if (data.dealTimeline !== undefined) payload.dealTimeline = data.dealTimeline as any
    if (data.downPaymentPercent !== undefined) payload.downPaymentPercent = data.downPaymentPercent
    if (data.additionalCriteria !== undefined) payload.additionalCriteria = data.additionalCriteria
    if (data.isArchived !== undefined) payload.isArchived = data.isArchived
    if (data.archiveReason !== undefined) payload.archiveReason = data.archiveReason
    // DEV-1061: причина архивации из справочника — бэкенд отклонит архивацию без неё.
    if (data.archiveReasonId !== undefined) payload.archiveReasonId = data.archiveReasonId
    // DEV-1918: канал привлечения (гейт clients:assign_agent на бэкенде).
    if (data.clientSourceType !== undefined) payload.clientSourceType = data.clientSourceType

    const response = await clientDataApi.clientDataServiceUpdateClient(data.id, payload)

    return mapClientFromAPI(response.data)
  },

  /**
   * Навесить тег на клиента (DEV-1062): «Прогрев» (WARMING), «Тест» (TEST).
   * POST /api/v1/clients/{clientId}/tags
   */
  async addClientTag(clientId: string, tagKey: string): Promise<ClientTag[]> {
    const response = await clientDataApi.clientDataServiceAddClientTag(clientId, { tagKey })
    return (response.data.tags || []) as ClientTag[]
  },

  /**
   * Разобрать заявку на дубль (DEV-1942): 'transfer' — клиент уходит заявившему
   * агенту, 'keep' — остаётся за текущим владельцем. В обоих случаях снимается
   * тег «Дубль» и карточка пропадает из виджета дублей.
   *
   * ⚠ Переназначение агента делает НЕ эта ручка, а agentsApi.setClientAgent —
   * инвариант «агент ⇔ канал Agent» живёт там (ADR-0051). При 'transfer' её
   * зовут первой.
   *
   * POST /api/v1/clients/{clientId}/duplicate/resolve
   */
  async resolveClientDuplicate(
    clientId: string,
    decision: 'transfer' | 'keep',
    agentId?: string,
  ): Promise<ClientTag[]> {
    const response = await clientDataApi.clientDataServiceResolveClientDuplicate(clientId, {
      decision,
      ...(agentId ? { agentId } : {}),
    })
    return (response.data.tags || []) as ClientTag[]
  },

  /**
   * Снять тег с клиента (DEV-1062).
   * DELETE /api/v1/clients/{clientId}/tags/{tagKey}
   */
  async removeClientTag(clientId: string, tagKey: string): Promise<ClientTag[]> {
    const response = await clientDataApi.clientDataServiceRemoveClientTag(clientId, tagKey)
    return (response.data.tags || []) as ClientTag[]
  },

  // TODO: Добавить deleteClient когда метод появится в API

  /**
   * Добавить заметку менеджера
   * POST /api/v1/clients/{id}/notes
   */
  async addManagerNote(clientId: string, content: string, isImportant?: boolean): Promise<void> {
    await clientDataApi.clientDataServiceAddManagerNote(clientId, { content, isImportant })
  },

  /**
   * Обновить заметку менеджера
   * PATCH /api/v1/clients/{clientId}/notes/{noteId}
   */
  async updateManagerNote(clientId: string, noteId: string, content: string): Promise<void> {
    await clientDataApi.clientDataServiceUpdateManagerNote(clientId, noteId, {
      content,
    })
  },

  /**
   * Удалить заметку менеджера
   * DELETE /api/v1/clients/{clientId}/notes/{noteId}
   */
  async deleteManagerNote(clientId: string, noteId: string): Promise<void> {
    await clientDataApi.clientDataServiceDeleteManagerNote(clientId, noteId)
  },

  // ===================== Client Tasks API (DEV-240) =====================

  /**
   * Получить задачи клиента
   * GET /api/v1/clients/{clientId}/tasks
   */
  async getClientTasks(clientId: string): Promise<ClientTask[]> {
    // DEV-1991. Источник — сервис задач: client_tasks и их ручки выпилены в О2.
    const tasks = await tasksServiceApi.listByEntity(TASK_ENTITY_CLIENT, clientId)

    return tasks.map((task) => mapServiceClientTask(clientId, task))
  },

  /**
   * Создать задачу клиента
   * POST /api/v1/clients/{clientId}/tasks
   */
  async createClientTask(data: CreateClientTaskRequest): Promise<ClientTask> {
    const task = await tasksServiceApi.create({
      entityType: TASK_ENTITY_CLIENT,
      entityId: data.clientId,
      description: data.description,
      dueDate: data.dueDate,
      dueHasTime: data.dueHasTime,
      reminderMinutes: data.reminderMinutes,
      assigneeManagerId: data.assigneeManagerId || undefined,
      taskTypeId: data.taskTypeId || undefined,
    })

    return mapServiceClientTask(data.clientId, task)
  },

  /**
   * Обновить задачу клиента (частично; completed=true — выполнить, false — вернуть)
   * PUT /api/v1/clients/{clientId}/tasks/{taskId}
   */
  async updateClientTask(data: UpdateClientTaskRequest): Promise<ClientTask> {
    // Задача адресуется своим id: клиент-владелец сервису известен.
    const task = await tasksServiceApi.update(data.taskId, {
      description: data.description,
      dueDate: data.dueDate,
      dueHasTime: data.dueHasTime,
      clearDueDate: data.clearDueDate,
      reminderMinutes: data.reminderMinutes,
      assigneeManagerId: data.assigneeManagerId || undefined,
      // Пустая строка снимает тип, undefined — не трогает: схлопывать их в
      // undefined значило бы «тип назад не убрать».
      taskTypeId: data.taskTypeId,
      status: data.completed === undefined ? undefined : statusFor(data.completed),
    })

    return mapServiceClientTask(data.clientId, task)
  },

  /**
   * Удалить задачу клиента
   * DELETE /api/v1/tasks/{taskId}
   */
  async deleteClientTask(taskId: string): Promise<void> {
    await tasksServiceApi.remove(taskId)
  },

  // ===================== Client Files API =====================

  /**
   * Получить список файлов клиента
   * GET /api/v1/clients/{clientId}/files
   */
  async getClientFiles(clientId: string): Promise<ClientFile[]> {
    const response = await clientDataApi.clientDataServiceListClientFiles(clientId)
    return (response.data.files || []).map(mapClientFileFromAPI)
  },

  /**
   * Добавить файл к клиенту
   * POST /api/v1/clients/{clientId}/files
   */
  async addClientFile(
    clientId: string,
    fileId: string,
    filename: string,
    displayName?: string,
    fileSize?: number,
    mimeType?: string,
    description?: string,
  ): Promise<ClientFile> {
    const response = await clientDataApi.clientDataServiceAddClientFile(clientId, {
      fileId,
      filename,
      displayName: displayName || filename,
      fileSize: fileSize?.toString(),
      mimeType,
      description,
    })
    return mapClientFileFromAPI(response.data.file)
  },

  /**
   * Обновить метаданные файла
   * PUT /api/v1/clients/{clientId}/files/{fileId}
   */
  async updateClientFile(
    clientId: string,
    fileId: string,
    displayName: string,
    description?: string,
  ): Promise<ClientFile> {
    const response = await clientDataApi.clientDataServiceUpdateClientFile(clientId, fileId, {
      displayName,
      description,
    })
    return mapClientFileFromAPI(response.data.file)
  },

  /**
   * Удалить файл
   * DELETE /api/v1/clients/{clientId}/files/{fileId}
   */
  async deleteClientFile(clientId: string, fileId: string): Promise<void> {
    await clientDataApi.clientDataServiceDeleteClientFile(clientId, fileId)
  },

  /**
   * Логировать скачивание файла
   * POST /api/v1/clients/{clientId}/files/{fileId}/download-log
   */
  async logClientFileDownload(clientId: string, fileId: string, filename: string): Promise<void> {
    await clientDataApi.clientDataServiceLogClientFileDownload(clientId, fileId, { filename })
  },

  // ===================== Client Change Logs API =====================

  /**
   * Получить историю изменений клиента
   * GET /api/v1/clients/{clientId}/change-logs
   */
  async getClientChangeLogs(
    clientId: string,
    options?: { actionType?: ClientChangeActionType; limit?: number; offset?: number },
  ): Promise<{ logs: ClientChangeLog[]; totalCount: number }> {
    const response = await clientDataApi.clientDataServiceListClientChangeLogs(
      clientId,
      options?.actionType as any,
      options?.limit,
      options?.offset,
    )
    const logs = (response.data.logs || []).map(mapClientChangeLogFromAPI)
    return {
      logs,
      totalCount: response.data.totalCount || 0,
    }
  },
}

/**
 * Маппер для преобразования лога изменений клиента из API формата
 */
function mapClientChangeLogFromAPI(log: any): ClientChangeLog {
  let details = undefined
  if (log.detailsJson) {
    try {
      details = JSON.parse(log.detailsJson)
    } catch {
      // ignore parse errors
    }
  }
  return {
    id: log.id,
    clientId: log.clientId,
    actionType: log.actionType,
    performedByManagerId: log.performedByManagerId,
    performedByManagerName: log.performedByManagerName,
    fieldName: log.fieldName,
    oldValue: log.oldValue,
    newValue: log.newValue,
    detailsJson: log.detailsJson,
    details,
    relatedEntityId: log.relatedEntityId,
    relatedEntityType: log.relatedEntityType,
    createdAt: log.createdAt,
  }
}

/**
 * Маппер для преобразования файла клиента из API формата
 */
function mapClientFileFromAPI(file: any): ClientFile {
  return {
    id: file.id,
    clientId: file.clientId,
    fileId: file.fileId,
    filename: file.filename,
    displayName: file.displayName || file.filename,
    description: file.description || '',
    fileSize: parseInt(file.fileSize) || 0,
    mimeType: file.mimeType || '',
    uploadedByManagerId: file.uploadedByManagerId,
    createdAt: file.createdAt,
    source: file.source || 'upload',
  }
}
